import React from 'react';
import { Button, Card, Nav, Tab } from 'react-bootstrap';
import Table from '../../table/redux/Table';
import { screeningClearanceRuleApi, screeningClearanceRulesAnalysisApi } from '../../apis';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import { useLocation, useNavigate } from 'react-router';
import './ScreeningClearanceRules.scss';
import Has from '../../auth/Has';
import { CAN_ADD_SCREENING_CLEARANCE_RULE, CAN_ADD_SCREENING_CLEARANCE_RULES_ANALYSIS } from '../../auth/permissions';
import {
  InternalApiEntityType,
  InternalApiScreeningClearanceRule,
  InternalApiScreeningClearanceRulesAnalysisWithRunInfo,
  InternalApiSortDirection,
} from '../../api-internal';
import { faPlus, faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SalvPage from '../../common/SalvPage';
import {
  getClearanceRulePath,
  getClearanceRulesAddPath,
  getClearanceRulesAnalysesPath,
  getConfigurationUrl,
} from '../../navigation/helper';
import { ConfigurationProduct } from '../../settings/configuration/Configuration';

type OrderByType = 'reason' | 'status' | undefined;

export function ScreeningClearanceRules() {
  const navigate = useNavigate();
  const { hash: locationHash, search } = useLocation();

  const params = new URLSearchParams(search);
  const sortDirection = params.get('direction')
    ? InternalApiSortDirection[params.get('direction') as keyof typeof InternalApiSortDirection]
    : undefined;
  const orderBy = (params.get('orderBy') || undefined) as OrderByType;

  const flipSortDirection = (newOrderBy: OrderByType) => {
    if (newOrderBy !== orderBy) {
      navigate({
        search: `?orderBy=${newOrderBy}&direction=DESC`,
      });
    } else if (sortDirection === InternalApiSortDirection.DESC) {
      navigate({
        search: `?orderBy=${newOrderBy}&direction=ASC`,
      });
    } else {
      navigate({
        search: '',
      });
    }
  };

  const fetchScreeningClearanceRules = (
    page: number,
    size: number,
    entityType: InternalApiEntityType,
    query?: string
  ) => {
    return screeningClearanceRuleApi.findAll({
      page,
      size,
      entityType,
      query,
      orderBy,
      direction: sortDirection,
    });
  };

  const fetchScreeningClearanceRulesAnalyses = (page: number, size: number, entityType: InternalApiEntityType) => {
    return screeningClearanceRulesAnalysisApi.findAllAnalysesWithRunInfo({
      page,
      size,
      entityType,
    });
  };

  const addRule = async (entityType: InternalApiEntityType) => {
    navigate(getClearanceRulesAddPath(entityType === InternalApiEntityType.PERSON ? 'person' : 'transaction'));
  };

  const addImpactAnalysis = async (entityType: InternalApiEntityType) => {
    navigate(`/screening/clearance-rules-analyses/add/${entityType.toLowerCase()}`);
  };

  function renderClearanceRules(entityType: InternalApiEntityType) {
    return (
      <Table
        title={'Clearance rules'}
        name={`rules-${entityType}`}
        searchable
        disableBoxShadow
        fetchContent={(page, size, query) => fetchScreeningClearanceRules(page, size, entityType, query)}
        header={() => (
          <tr>
            <th
              onClick={() => flipSortDirection('reason')}
              className={'pointer'}
              data-testid={`rules-${entityType}-reason-sort`}
            >
              <div>
                {orderBy === 'reason' ? (
                  sortDirection === InternalApiSortDirection.DESC ? (
                    <FontAwesomeIcon icon={faSortDown} aria-label='descending' />
                  ) : (
                    <FontAwesomeIcon icon={faSortUp} aria-label='ascending' />
                  )
                ) : (
                  <FontAwesomeIcon icon={faSort} aria-label='sort' />
                )}
                <span className='mx-2'>Reason</span>
              </div>
            </th>
            <th>Details</th>
            <th>Date added</th>
            <th onClick={() => flipSortDirection('status')} className={'pointer'}>
              <div>
                {orderBy === 'status' ? (
                  sortDirection === InternalApiSortDirection.DESC ? (
                    <FontAwesomeIcon icon={faSortDown} aria-label='descending' />
                  ) : (
                    <FontAwesomeIcon icon={faSortUp} aria-label='ascending' />
                  )
                ) : (
                  <FontAwesomeIcon icon={faSort} aria-label='sort' />
                )}
                <span className='mx-2'>Status</span>
              </div>
            </th>
            <th>Author</th>
          </tr>
        )}
        actions={() => (
          <Has allPermissions={CAN_ADD_SCREENING_CLEARANCE_RULE}>
            <Button
              variant='info'
              size='sm'
              className='btn-white'
              onClick={() => addRule(entityType)}
              data-testid={`new-rule-${entityType}`}
            >
              <FontAwesomeIcon icon={faPlus} className='me-2' />
              New rule
            </Button>
          </Has>
        )}
        body={(rules: InternalApiScreeningClearanceRule[]) =>
          rules.length > 0 &&
          rules.map((rule) => (
            <tr
              key={rule.handle}
              onClick={() => navigate(getClearanceRulePath(rule.handle!))}
              className='clickable-row'
              data-testid='rule-row'
            >
              <td>{rule.reason}</td>
              <td>{rule.details}</td>
              <td>
                <FormattedDateTime date={rule.lastUpdatedTime} />
              </td>
              <td className={rule.enabled ? 'active' : 'inactive'}>{rule.enabled ? 'Active' : 'Inactive'}</td>
              <td>{rule.lastUpdatedBy && rule.lastUpdatedBy.name}</td>
            </tr>
          ))
        }
      />
    );
  }

  function renderImpactAnalyses(entityType: InternalApiEntityType) {
    return (
      <Table
        title={'Impact analyses'}
        name={`analyses-${entityType}`}
        disableBoxShadow={true}
        fetchContent={(page, size) => fetchScreeningClearanceRulesAnalyses(page, size, entityType)}
        header={() => (
          <tr>
            <th>Name and description</th>
            <th>Last run</th>
            <th>Created by</th>
          </tr>
        )}
        actions={() => (
          <Has allPermissions={CAN_ADD_SCREENING_CLEARANCE_RULES_ANALYSIS}>
            <Button
              variant='info'
              size='sm'
              className='btn-white'
              onClick={() => addImpactAnalysis(entityType)}
              data-testid={`new-analysis-${entityType}`}
            >
              <FontAwesomeIcon icon={faPlus} className='me-2' />
              New analysis
            </Button>
          </Has>
        )}
        body={(analyses: InternalApiScreeningClearanceRulesAnalysisWithRunInfo[]) =>
          analyses.length > 0 &&
          analyses.map((analysis) => (
            <tr
              key={analysis.id}
              onClick={() => navigate(getClearanceRulesAnalysesPath(analysis.id!.toString()))}
              className='clickable-row'
              data-testid='rule-row'
            >
              <td>
                {analysis.name}
                <br />
                <span className={'text-muted'}>{analysis.description}</span>
              </td>
              <td>{analysis.lastRunTime ? <FormattedDateTime date={analysis.lastRunTime} /> : '-'}</td>
              <td>{analysis.createdBy?.name || '-'}</td>
            </tr>
          ))
        }
      />
    );
  }

  return (
    <SalvPage
      title={'Screening clearance rules'}
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.SCREENING),
      }}
    >
      <Tab.Container
        defaultActiveKey={locationHash.substring(1) === 'transaction' ? 'transaction' : 'person'}
        onSelect={(value) => navigate('#' + value, { replace: true })}
      >
        <Card className='screening-clearance-rules'>
          <Card.Header className='top-level-header'>
            <Nav>
              <Nav.Item>
                <Nav.Link eventKey='person'>Person</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey='transaction'>Transaction</Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>
              <Tab.Pane eventKey='person' title='Person' mountOnEnter unmountOnExit>
                {renderClearanceRules(InternalApiEntityType.PERSON)}
                {renderImpactAnalyses(InternalApiEntityType.PERSON)}
              </Tab.Pane>
              <Tab.Pane eventKey='transaction' title='Transaction' unmountOnExit mountOnEnter>
                {renderClearanceRules(InternalApiEntityType.TRANSACTION)}
                {renderImpactAnalyses(InternalApiEntityType.TRANSACTION)}
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>
    </SalvPage>
  );
}

export default ScreeningClearanceRules;
